import { useMemo } from "react";
import {
  getAlbumChildIndex,
  getAlbumParentWid,
  isAlbumChildMessage,
  isAlbumStubMessage,
  isImageMediaForAlbum,
} from "./albumMessageUtils";

const MIN_GROUP_SIZE = 2;
const MAX_INTERVAL_MS = 60000;
const LEGACY_ALBUM_WINDOW_MS = 10 * 60 * 1000;
const ALBUM_CAPTION_WINDOW_MS = 15 * 60 * 1000;

const IMAGE_MEDIA_TYPES = new Set(["image", "imagemessage"]);
const PLACEHOLDER_BODIES = new Set([
  "imagem",
  "image",
  "[imagem]",
  "[image]",
  "sticker",
  "áudio",
  "audio",
  "vídeo",
  "video",
  "documento",
  "document",
  "mídia",
  "media",
  "📎 mídia",
  "álbum",
  "album",
  "[álbum]",
  "[album]",
]);

const isImageMediaType = (mediaType) => {
  const mt = String(mediaType || "").toLowerCase();
  return IMAGE_MEDIA_TYPES.has(mt) || mt.includes("image");
};

const mediaUrlLooksLikeImage = (url) =>
  !!url && /\.(jpe?g|png|gif|webp|bmp)(\?|#|$)/i.test(String(url));

const COMMON_COUNTRY_CODES = ["55", "1", "44", "33", "49", "39", "34"];

function isLikelyLid(value) {
  if (!value) return false;
  const s = String(value).toLowerCase();
  if (s.includes("@lid") || s.startsWith("lid_")) return true;
  const digits = s.replace(/\D/g, "");
  if (digits.length >= 13 && digits.length <= 15) {
    return !COMMON_COUNTRY_CODES.some((code) => digits.startsWith(code));
  }
  return false;
}

function resolveStableSenderPhone(msg) {
  try {
    const raw =
      typeof msg?.dataJson === "string" ? JSON.parse(msg.dataJson) : msg?.dataJson;
    const alt = raw?.senderAlt || raw?.SenderAlt;
    if (alt && String(alt).includes("@s.whatsapp.net")) {
      const d = phoneDigitsFromJid(alt);
      if (d && !isLikelyLid(d)) return d;
    }
  } catch {
    /* ignore */
  }

  const part = extractParticipantJid(msg);
  if (part && !String(part).toLowerCase().includes("@lid")) {
    const d = phoneDigitsFromJid(part);
    if (d && !isLikelyLid(d)) return d;
  }

  const contactDigits = String(msg.contact?.number || "").replace(/\D/g, "");
  if (contactDigits.length >= 10 && contactDigits.length <= 13 && !isLikelyLid(contactDigits)) {
    return contactDigits;
  }

  return null;
}

function extractParticipantJid(msg) {
  try {
    const raw =
      typeof msg?.dataJson === "string" ? JSON.parse(msg.dataJson) : msg?.dataJson;
    const alt = raw?.senderAlt || raw?.SenderAlt;
    if (alt && String(alt).includes("@s.whatsapp.net")) {
      return String(alt).trim();
    }
  } catch {
    /* ignore */
  }
  if (msg?.participant) {
    const p = String(msg.participant).trim();
    const local = p.split("@")[0] || "";
    if (!p.toLowerCase().endsWith("@lid") && !isLikelyLid(local)) return p;
  }
  try {
    const raw =
      typeof msg?.dataJson === "string" ? JSON.parse(msg.dataJson) : msg?.dataJson;
    const p = raw?.key?.participant || raw?.participant;
    return p ? String(p).trim() : null;
  } catch {
    return null;
  }
}

function phoneDigitsFromJid(jid) {
  if (!jid) return "";
  const local = String(jid).split("@")[0] || "";
  const digits = local.replace(/\D/g, "");
  return digits.length >= 10 ? digits : "";
}

function normalizeGroupSenderKey(participantJid) {
  if (!participantJid) return null;
  const s = participantJid.toLowerCase();
  const local = s.split("@")[0] || "";
  if (s.endsWith("@lid")) return `lid:${local.replace(/\D/g, "") || local}`;
  const digits = phoneDigitsFromJid(s);
  if (digits) return `pn:${digits}`;
  return `jid:${s}`;
}

function messageIsInGroup(msg, ticketIsGroup) {
  if (ticketIsGroup) return true;
  const jid = String(msg?.remoteJid || "").toLowerCase();
  return jid.endsWith("@g.us");
}

function getSender(msg, isGroup) {
  if (msg.fromMe) return "__me__";

  const inGroupChat = messageIsInGroup(msg, isGroup);

  if (!inGroupChat) {
    if (msg.contactId != null) return `cid:${msg.contactId}`;
    if (msg.contact?.id != null) return `cid:${msg.contact.id}`;
  }

  const stablePhone = resolveStableSenderPhone(msg);
  if (stablePhone) return `pn:${stablePhone}`;

  if (msg.contactId != null) return `cid:${msg.contactId}`;
  if (msg.contact?.id != null) return `cid:${msg.contact.id}`;

  const part = extractParticipantJid(msg);
  if (inGroupChat && part) {
    const partKey = normalizeGroupSenderKey(part);
    if (partKey) return partKey;
  }

  return "__unknown__";
}

function getTimestamp(msg) {
  return new Date(msg.createdAt).getTime();
}

function findAlbumCaption(group, messagesList, getSenderFn) {
  if (!group?.length) return null;
  const last = group[group.length - 1];
  const lastTime = getTimestamp(last);
  const sender = getSenderFn(last);
  const lastIdx = messagesList.findIndex((m) => m.id === last.id);
  if (lastIdx < 0) return null;

  for (let i = lastIdx + 1; i < messagesList.length; i++) {
    const m = messagesList[i];
    if (getTimestamp(m) - lastTime > ALBUM_CAPTION_WINDOW_MS) break;
    if (getSenderFn(m) !== sender) break;
    if (m.mediaUrl) break;
    const body = (m.body || "").trim();
    if (!body) continue;
    const lower = body.toLowerCase();
    if (PLACEHOLDER_BODIES.has(lower)) continue;
    return { body, messageId: m.id };
  }
  return null;
}

/**
 * Agrupa imagens consecutivas e fotos de álbum WhatsApp (messageAssociation / stub legado).
 */
const useImageGrouping = (messagesList, enabled = true, isGroup = false) => {
  return useMemo(() => {
    const groupMap = new Map();

    if (!enabled || !messagesList || messagesList.length === 0) {
      return groupMap;
    }

    const usedIds = new Set();
    const captionMessageIds = new Set();
    const senderOf = (msg) => getSender(msg, isGroup);

    const registerGroup = (images, groupId, { isAlbum = false, caption = null, captionMessageId = null } = {}) => {
      if (!images?.length) return;
      const sorted = [...images].sort(
        (a, b) =>
          getAlbumChildIndex(a) - getAlbumChildIndex(b) ||
          getTimestamp(a) - getTimestamp(b)
      );
      const minSize = isAlbum ? 1 : MIN_GROUP_SIZE;
      if (sorted.length < minSize) return;

      for (const img of sorted) {
        usedIds.add(String(img.id));
      }
      if (captionMessageId != null) {
        captionMessageIds.add(String(captionMessageId));
      }

      for (let i = 0; i < sorted.length; i++) {
        groupMap.set(String(sorted[i].id), {
          groupId,
          messages: sorted,
          isFirst: i === 0,
          isGrouped: true,
          isAlbum,
          caption: i === 0 ? caption : null,
          captionMessageId: i === 0 ? captionMessageId : null,
        });
      }
    };

    // 1) Álbum via messageAssociation (proto WhatsApp)
    const albumBuckets = new Map();
    for (const msg of messagesList) {
      if (!isImageMediaForAlbum(msg)) continue;
      const parent = getAlbumParentWid(msg);
      if (!parent) continue;
      if (!albumBuckets.has(parent)) albumBuckets.set(parent, []);
      albumBuckets.get(parent).push(msg);
    }
    for (const [parent, imgs] of albumBuckets) {
      const cap = findAlbumCaption(imgs, messagesList, senderOf);
      registerGroup(imgs, `album-${parent}`, {
        isAlbum: true,
        caption: cap?.body || null,
        captionMessageId: cap?.messageId ?? null,
      });
    }

    // 2) Álbum legado: stub "Álbum" + imagens nos minutos seguintes (tickets antigos)
    for (const stub of messagesList) {
      if (!isAlbumStubMessage(stub) || usedIds.has(String(stub.id))) continue;
      const stubTime = getTimestamp(stub);
      const sender = senderOf(stub);
      const images = messagesList.filter(
        (m) =>
          !usedIds.has(String(m.id)) &&
          isImageMediaForAlbum(m) &&
          senderOf(m) === sender &&
          getTimestamp(m) >= stubTime &&
          getTimestamp(m) <= stubTime + LEGACY_ALBUM_WINDOW_MS
      );
      if (images.length > 0) {
        const cap = findAlbumCaption(images, messagesList, senderOf);
        registerGroup(images, `legacy-album-${stub.wid || stub.id}`, {
          isAlbum: true,
          caption: cap?.body || null,
          captionMessageId: cap?.messageId ?? null,
        });
      }
    }

    // 3) Agrupamento consecutivo (comportamento original)
    const groups = [];
    let currentGroup = [];

    const getBasename = (url) => {
      try {
        return decodeURIComponent(url.split("/").pop().split("?")[0]).trim();
      } catch {
        return url.split("/").pop().split("?")[0].trim();
      }
    };

    const isLikelyAutoBody = (body, mediaUrl) => {
      const b = (body || "").trim();
      if (!b) return true;
      const lower = b.toLowerCase();
      if (PLACEHOLDER_BODIES.has(lower)) return true;
      if (/^[A-Fa-f0-9\-_]{16,}(\.[a-z0-9]+)?$/i.test(b)) return true;
      const base = getBasename(mediaUrl || "");
      if (base && lower === base.toLowerCase()) return true;
      if (/\.(jpe?g|png|gif|webp|bmp)(\?|#|$)/i.test(b) && b.length < 200) return true;
      if (base && base.toLowerCase().endsWith(lower)) return true;
      return false;
    };

    const hasCaption = (msg) => {
      const body = (msg.body || "").trim();
      if (!body) return false;
      if (isLikelyAutoBody(body, msg.mediaUrl)) return false;
      return true;
    };

    const isImageMessage = (msg) => {
      if (!msg || msg.isDeleted || !msg.mediaUrl) return false;
      if (isAlbumChildMessage(msg)) return true;

      const mt = String(msg.mediaType || "").toLowerCase();
      const urlIsImage = mediaUrlLooksLikeImage(msg.mediaUrl);
      const isImage =
        isImageMediaType(msg.mediaType) ||
        (mt === "media" && urlIsImage) ||
        urlIsImage;

      if (!isImage) return false;
      if (hasCaption(msg)) return false;
      return true;
    };

    const isIgnorableBetweenImages = (msg) => {
      if (!msg || msg.isDeleted) return true;
      if (isAlbumStubMessage(msg)) return true;
      const mt = String(msg.mediaType || "").toLowerCase();
      if (mt === "reactionmessage" || mt === "reaction") return true;
      if (!msg.mediaUrl) {
        const body = (msg.body || "").trim();
        if (!body) return true;
        if (PLACEHOLDER_BODIES.has(body.toLowerCase())) return true;
        if (/^[A-Fa-f0-9\-_]{8,}$/.test(body)) return true;
      }
      return false;
    };

    for (let i = 0; i < messagesList.length; i++) {
      const msg = messagesList[i];
      if (usedIds.has(String(msg.id))) continue;

      if (isIgnorableBetweenImages(msg)) continue;

      if (isImageMessage(msg)) {
        if (currentGroup.length === 0) {
          currentGroup.push(msg);
        } else {
          const lastMsg = currentGroup[currentGroup.length - 1];
          const sameSender = senderOf(msg) === senderOf(lastMsg);
          const withinInterval =
            Math.abs(getTimestamp(msg) - getTimestamp(lastMsg)) <= MAX_INTERVAL_MS;

          if (sameSender && withinInterval) {
            currentGroup.push(msg);
          } else {
            if (currentGroup.length >= MIN_GROUP_SIZE) {
              groups.push([...currentGroup]);
            }
            currentGroup = [msg];
          }
        }
      } else {
        if (currentGroup.length >= MIN_GROUP_SIZE) {
          groups.push([...currentGroup]);
        }
        currentGroup = [];
      }
    }

    if (currentGroup.length >= MIN_GROUP_SIZE) {
      groups.push([...currentGroup]);
    }

    for (const group of groups) {
      registerGroup(group, `img-group-${group[0].id}`, { isAlbum: false });
    }

    groupMap.captionMessageIds = captionMessageIds;
    return groupMap;
  }, [messagesList, enabled, isGroup]);
};

export default useImageGrouping;
